import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type RingStrategy = 'simultaneous' | 'sequential';

export interface CompanyCallRingLevel {
  id: string;
  level: number;
  name: string;
  timeout_seconds: number;
  user_ids: string[];
  active: boolean;
  business_unit: string;
  ring_strategy: RingStrategy;
}

/** Configuração unificada (sem separação BR/EUA) do canal do número da empresa. */
const UNIFIED_BU = 'all';

export function useCompanyCallRingSettings() {
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['company-call-ring-settings', UNIFIED_BU],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('company_call_ring_settings' as any)
        .select('*')
        .eq('business_unit', UNIFIED_BU)
        .order('level');

      if (error) throw error;
      return data as unknown as CompanyCallRingLevel[];
    },
  });

  const updateSettings = useMutation({
    mutationFn: async (levels: Partial<CompanyCallRingLevel>[]) => {
      const results = await Promise.all(
        levels.map((level) =>
          supabase
            .from('company_call_ring_settings' as any)
            .upsert(
              {
                level: level.level!,
                name: level.name!,
                timeout_seconds: level.timeout_seconds!,
                user_ids: level.user_ids!,
                active: level.active ?? true,
                ring_strategy: level.ring_strategy ?? 'simultaneous',
                business_unit: UNIFIED_BU,
              },
              { onConflict: 'level,business_unit' }
            )
            .select()
        )
      );

      const errors = results.filter((r) => r.error);
      if (errors.length > 0) throw errors[0].error;

      return results.map((r) => r.data).flat();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-call-ring-settings', UNIFIED_BU] });
      toast.success('Configuração salva com sucesso');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao salvar configuração: ${error.message}`);
    },
  });

  return {
    settings,
    isLoading,
    updateSettings: updateSettings.mutate,
    isUpdating: updateSettings.isPending,
  };
}
